import { localDateInput } from './dateUtils';
import { parseJsonObject } from './json';

export interface QuotationLine {
  item: string;
  specification: string;
  unit: string;
  quantity: string;
  unitPrice: string;
  amount: string;
}

export interface QuotationDraft {
  date: string;
  quotationNumber: string;
  clientName: string;
  contact: string;
  email: string;
  scope: string;
  exclusions: string;
  note: string;
  lines: QuotationLine[];
  finalAmount: string;
  validityDays: string;
}

interface StageRef {
  stageId: string;
  stageName: string;
}

interface StageDataRef {
  stageId: string;
  dataJson?: string | null;
}

const text = (value: unknown) => (value === undefined || value === null ? '' : String(value).trim());

export const blankLine = (): QuotationLine => ({
  item: '',
  specification: '',
  unit: '',
  quantity: '',
  unitPrice: '',
  amount: '',
});

export const lineAmount = (line: QuotationLine): number => {
  if (line.amount.trim() !== '') {
    const written = Number(line.amount);
    if (Number.isFinite(written)) return written;
  }
  const quantity = Number(line.quantity);
  const price = Number(line.unitPrice);
  if (line.quantity.trim() !== '' && line.unitPrice.trim() !== '' && Number.isFinite(quantity) && Number.isFinite(price))
    return quantity * price;
  return 0;
};

export const subTotalOf = (lines: QuotationLine[]) =>
  lines.reduce((sum, line) => sum + lineAmount(line), 0);

const stageValues = (
  name: string,
  stages: StageRef[],
  stageData: StageDataRef[]
): Record<string, unknown> => {
  const stage = stages.find((item) => item.stageName.trim().toLowerCase() === name);
  if (!stage) return {};
  return parseJsonObject(stageData.find((row) => row.stageId === stage.stageId)?.dataJson);
};

const asLines = (value: unknown): QuotationLine[] => {
  if (!Array.isArray(value)) return [];
  return value.map((row) => {
    const record = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
    const quantity = text(record.quantity);
    const unitPrice = text(record.unitPrice);
    const amount = text(record.amount);
    const item = text(record.item);
    const specification = text(record.specification);
    const line: QuotationLine = {
      item,
      specification,
      unit: text(record.unit),
      quantity,
      unitPrice,
      amount,
    };
    if (!amount && quantity && unitPrice) line.amount = String(lineAmount(line));
    return line;
  });
};

export const buildQuotationDraft = (input: {
  formValues: Record<string, any>;
  enquiry: Record<string, any>;
  taskName?: string;
  stages: StageRef[];
  stageData: StageDataRef[];
  today?: string;
}): QuotationDraft => {
  const boq = stageValues('prepare & check boq', input.stages, input.stageData);
  const verified = stageValues('verify site information', input.stages, input.stageData);
  const lines = asLines(boq.boqLines);
  const scope = text(input.enquiry.scopeSummary);
  const exclusions = text(input.formValues.exclusions) || text(verified.exclusions);

  return {
    date: input.today ?? localDateInput(),
    quotationNumber: text(input.formValues.quotationNumber) || text(input.enquiry.enquiryNumber) || text(input.taskName),
    clientName: text(input.enquiry.client),
    contact: text(input.enquiry.contact),
    email: '',
    scope,
    exclusions,
    note: exclusions || scope,
    lines: lines.length > 0 ? lines : [blankLine()],
    finalAmount: String(subTotalOf(lines)),
    validityDays: text(input.formValues.validity) || text(boq.validityDays),
  };
};
